import React, { FC } from 'react';
import {
  Filter,
  TextInput,
  SelectInput,
  DateInput,
  FilterProps,
  ReferenceInput,
} from 'react-admin';
import { PhaseTypeEnum } from '@vatos-pas/common';
import { LazyReferenceInput } from 'components/LazyAutoCompleteInput';
import { UserRoleMultiReferenceInput } from 'components/UserRoleMultiReferenceInput';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_SEE_RECEIVABLE_INVOICE_COLUMNS } from 'providers/permissions';
import { ReceivablePhaseSelectInput } from '../components/ReceivablePhaseSelectInput';

const BOOLEAN_FILTER_CHOICES = [
  { val: true, text: 'Yes' },
  { val: false, text: 'No' },
];

export const JobPhaseReceivableSheetFilter: FC<FilterProps> = props => {
  const { hasPermission } = PermissionsProvider.useContainer();
  const canSeeInvoiceColumns = hasPermission(
    CAN_SEE_RECEIVABLE_INVOICE_COLUMNS,
  );

  return (
    <Filter {...props}>
      <UserRoleMultiReferenceInput
        label="Approved By"
        source="approvedById"
        roles={['Supervisor', 'SuperAdmin', 'RegionalAdmin']}
        referenceForGetOne="user"
        data-cy="JobPhaseReceivableSheetFilterApprovedBy"
        resettable
        alwaysOn
      />
      <ReferenceInput
        label="Phase"
        source="phaseId"
        reference="phase"
        data-cy="JobPhaseReceivableSheetFilterPhase"
        perPage={Number.MAX_SAFE_INTEGER}
        sort={{ field: 'priority', order: 'ASC' }}
        filter={{
          'phaseType||$in': [
            PhaseTypeEnum.Spraying,
            PhaseTypeEnum.Finish,
            PhaseTypeEnum.FinalPaint,
          ],
        }}
        allowEmpty={false}
        resettable
        alwaysOn
      >
        <ReceivablePhaseSelectInput source="phaseId" label="Phase" />
      </ReferenceInput>
      <ReferenceInput
        label="Region"
        source="regionId"
        reference="region"
        data-cy="JobPhaseReceivableSheetFilterRegion"
        perPage={Number.MAX_SAFE_INTEGER}
        sort={{ field: 'name', order: 'ASC' }}
        allowEmpty={false}
        resettable
        alwaysOn
      >
        <SelectInput optionText="name" optionValue="id" />
      </ReferenceInput>
      <LazyReferenceInput
        label="Builder"
        source="builderId"
        reference="builder"
        data-cy="JobPhaseReceivableSheetFilterBuilder"
        filterToQuery={value => (value ? { name: value } : {})}
        sort={{ field: 'name', order: 'ASC' }}
        optionText="name"
        optionValue="id"
        resettable
        alwaysOn
      />
      <LazyReferenceInput
        label="Subdivision"
        source="subdivisionId"
        reference="subdivision"
        data-cy="JobPhaseReceivableSheetFilterSubdivision"
        filterToQuery={value => (value ? { name: value } : {})}
        sort={{ field: 'name', order: 'ASC' }}
        optionText="name"
        optionValue="id"
        resettable
        alwaysOn
      />
      <TextInput
        label="Lot"
        source="lot||$eq"
        data-cy="JobPhaseReceivableSheetFilterLot"
        resettable
      />
      <TextInput
        label="Block"
        source="block||$cont"
        data-cy="JobPhaseReceivableSheetFilterBlock"
        resettable
      />
      {canSeeInvoiceColumns && (
        <TextInput
          label="Internal Invoice #"
          source="internalInvoiceNumber||$cont"
          data-cy="JobPhaseReceivableSheetFilterInternalInvoice"
          resettable
        />
      )}
      <DateInput
        style={{ minWidth: 200 }}
        label="Approval On or After"
        source="dateApproved||$gte"
        data-cy="JobPhaseReceivableSheetFilterApprovalAfter"
        alwaysOn
      />
      <DateInput
        style={{ minWidth: 200 }}
        label="Approval On or Before"
        source="dateApproved||$lte"
        data-cy="JobPhaseReceivableSheetFilterApprovalBefore"
        alwaysOn
      />
      {canSeeInvoiceColumns && (
        <SelectInput
          style={{ minWidth: 200 }}
          label="SupplyPro ClickOff"
          source="supplyProIntegrated||$eq"
          data-cy="JobPhaseReceivableSheetFilterSupplyPro"
          choices={BOOLEAN_FILTER_CHOICES}
          emptyText="All"
          optionValue="val"
          optionText="text"
          alwaysOn
        />
      )}
      {canSeeInvoiceColumns && (
        <SelectInput
          label="Billed"
          source="billed||$eq"
          data-cy="JobPhaseReceivableSheetFilterBilled"
          choices={BOOLEAN_FILTER_CHOICES}
          emptyText="All"
          optionValue="val"
          optionText="text"
          alwaysOn
        />
      )}
      {canSeeInvoiceColumns && (
        <SelectInput
          style={{ minWidth: 200 }}
          label="Empty Internal Invoice"
          // Synthetic key: Yes/No map to $isnull/$notnull in dataProvider, since a
          // single source can't bind both null operators (and $isnull ignores its
          // value, so "No" can't be expressed as $isnull=false).
          source="emptyInternalInvoice"
          data-cy="JobPhaseReceivableSheetFilterEmptyInvoice"
          choices={BOOLEAN_FILTER_CHOICES}
          emptyText="All"
          optionValue="val"
          optionText="text"
        />
      )}
    </Filter>
  );
};
